<template>
  <v-container class="py-md-8" style="max-width: 800px !important">
    <h2 class="text-h2 flex-fill px-3 mb-6">Survey Reporting</h2>
    <v-breadcrumbs
      class="px-3 mb-4 pt-3"
      :items="breadcrumbs"
      divider=">"
    ></v-breadcrumbs>
    <v-alert
      v-if="aggAlert"
      color="grey lighten-1 black--text"
      class="mb-2"
      border="left"
    >
      <div class="d-flex align-center pl-2">
        <v-progress-circular
          indeterminate
          color="primary"
          size="16"
          width="2"
        ></v-progress-circular>
        <div class="flex-fill ml-2">
          Refreshing Aggregates... This could take a while.
        </div>
      </div>
    </v-alert>
    <v-card
      :disabled="loading"
      class="d-flex mb-2"
      v-for="(report, i) in survey.reports"
      :key="i"
    >
      <v-card-text class="d-flex align-center">
        <div class="flex-fill d-flex align-center">
          <v-icon large class="mr-4">${{ report.icon }}</v-icon>
          <div>
            <div class="text-subtitle-1">{{ report.name }}</div>
            <v-chip small class="mr-1 mb-1">
              <span class="font-weight-bold mr-1"> Type:</span>
              {{ report.type }}
            </v-chip>
            <v-chip
              small
              class="mr-1 mb-1"
              v-if="report.groupBy && report.groupBy.length > 0"
            >
              <span class="font-weight-bold mr-1"> Group By:</span>
              {{ report.groupBy.join(", ") }}
            </v-chip>
            <v-chip small class="mr-1 mb-1">
              <span class="font-weight-bold mr-1">Last Generated:</span>
              {{ report.formattedGeneratedAt }}
            </v-chip>
          </div>
        </div>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>$more</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item class="d-flex" @click="edit(i)">
              <v-icon class="mr-2">$edit</v-icon>
              Edit Report
            </v-list-item>
            <v-list-item class="d-flex">
              <v-icon class="mr-2">$delete</v-icon>
              <div>Delete Report</div>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-text>
      <div></div>
    </v-card>

    <div
      v-if="!survey.reports || survey.reports.length === 0"
      class="rounded pa-4 grey lighten-1 mb-2 text-center text-caption lighten--text-2 black--text"
    >
      No Reports have been added
    </div>

    <v-divider class="my-4"></v-divider>
    <v-btn
      color="primary"
      class="my-2"
      block
      @click="() => edit(-1)"
      :loading="loading"
    >
      Add Report
    </v-btn>
    <v-btn
      color="accent"
      class="my-2"
      block
      :to="{
        name: 'public_report',
        params: {
          id: $route.params.id,
          tenantId,
        },
      }"
      target="_blank"
      :disabled="loading"
    >
      View Reports
    </v-btn>
    <v-btn
      class="my-2"
      color="accent"
      block
      @click="refreshAggs"
      :disabled="loading"
    >
      Refresh Reports
    </v-btn>

    <v-dialog
      v-model="openEditor"
      max-width="600px"
      :persistent="loading"
      scrollable
    >
      <ValidationObserver ref="observer" tag="form" @submit.prevent="onSubmit">
        <v-card>
          <dialog-title
            v-model="openEditor"
            :title="`${isNew ? 'Add' : 'Edit'} Report`"
            :disabled="loading"
            :loading="loading"
          ></dialog-title>
          <v-card-subtitle>
            Fill out the form below to configure a <strong>report</strong>.
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-card-text class="pt-6">
            <v-row dense>
              <v-col cols="8">
                <ValidationProvider
                  rules="required|min:2|max:255"
                  v-slot="{ errors }"
                  vid="Name"
                >
                  <v-text-field
                    v-model="item.name"
                    label="Name*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="4">
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  vid="Type"
                >
                  <v-select
                    v-model="item.type"
                    label="Type*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                    :items="typeOptions"
                  ></v-select>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <div class="px-4 py-3 grey lighten-2 rounded mb-4">
                  <div class="font-weight-bold">Icon:</div>
                  <v-btn
                    type="button"
                    :elevation="item.icon !== option ? 0 : 4"
                    v-for="option in iconOptions"
                    icon
                    class="ma-1"
                    :key="option"
                    @click="item.icon = option"
                    :class="{ 'white black--text': item.icon === option }"
                    ><v-icon style="font-size: 1.25rem !important"
                      >${{ option }}</v-icon
                    ></v-btn
                  >
                </div>
              </v-col>
              <v-col cols="12">
                <div class="px-4 py-3 grey lighten-2 rounded-t">
                  <div class="font-weight-bold mb-2">
                    Aggregate Grouping Keys:
                  </div>
                  <v-chip
                    class="ma-1"
                    v-for="(key, i) in item.groupBy"
                    :key="key"
                    color="primary"
                    @click="removeGrouping(i)"
                    >{{ key }}
                    <v-icon small class="ml-2">$close</v-icon></v-chip
                  >
                  <i v-if="item.groupBy.length === 0" class="text-caption">
                    - No Groupings Added -
                  </i>
                </div>

                <div class="d-flex">
                  <v-text-field
                    dense
                    v-model="newGrouping"
                    placeholder="Add Grouping Key"
                    outlined
                    class="rounded-t-0 rounded-r-0 flex-fill"
                    @keydown.enter.prevent="addGrouping()"
                    hide-details=""
                  ></v-text-field>
                  <v-btn
                    type="button"
                    tile
                    elevation="0"
                    color="success"
                    class="rounded-br h-auto font-weight-bold"
                    @click="addGrouping"
                    ><v-icon class="mr-1">$add</v-icon> Add</v-btn
                  >
                </div>
                <div class="text-caption px-3">
                  Note: Only single key groupings are supported.
                </div>
              </v-col>
              <v-col cols="12" v-if="item.groupBy.length > 0">
                <v-divider class="dashed-h mt-4 mb-2"></v-divider>
                <div class="d-flex align-center">
                  <h4 class="text-h6 flex-fill">Grouping Comparisons</h4>

                  <v-btn
                    color="success"
                    outlined
                    elevation="0"
                    class="font-weight-bold"
                  >
                    <v-icon class="mr-1">$add</v-icon>Add
                  </v-btn>
                </div>

                <div
                  v-if="
                    !item.groupComparisons || item.groupComparisons.length === 0
                  "
                  class="px-4 py-3 my-4 grey lighten-2 rounded"
                >
                  <i class="text-caption">
                    - No Groupings Comparisons Added -
                  </i>
                </div>

                <div
                  v-for="(comparison, i) in item.groupComparisons"
                  :key="i"
                  class="mt-4 mb-6"
                >
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th
                            class="text-left grey darken-4 white--text rounded-tl"
                          >
                            Question Key
                          </th>
                          <th
                            class="text-left grey darken-4 white--text rounded-tr"
                          >
                            Criteria
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(setting, ii) in comparison"
                          :key="ii"
                          :class="{ 'grey lighten-3': ii % 2 }"
                        >
                          <td>{{ setting.key }}</td>
                          <td>
                            <v-chip
                              small
                              color="primary"
                              class="mr-1 mb-1"
                              v-for="(grouping, iii) in setting.groupedOn"
                              :key="iii"
                            >
                              <span class="font-weight-bold mr-1">
                                {{ grouping.key }}:</span
                              >
                              {{ grouping.value }}
                            </v-chip>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <th colspan="2" class="grey text-right rounded-b pa-1">
                          <v-btn
                            small
                            tile
                            elevation="0"
                            class="mr-1"
                            @click="editComparison(i)"
                          >
                            <v-icon color="grey darken-3" x-small class="mr-1"
                              >$edit</v-icon
                            >
                            Edit
                          </v-btn>
                          <v-btn small tile elevation="0" class="rounded-br">
                            <v-icon color="grey darken-3" x-small class="mr-1"
                              >$delete</v-icon
                            >
                            Delete
                          </v-btn>
                        </th>
                      </tfoot>
                    </template>
                  </v-simple-table>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn type="button" elevation="0" text @click="openEditor = false"
              >Cancel</v-btn
            >
            <v-btn type="submit" color="primary">Save</v-btn>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog
      v-model="openComparisonEditor"
      max-width="650px"
      :persistent="loading"
      scrollable
    >
      <ValidationObserver
        ref="comparisonObserver"
        tag="form"
        @submit.prevent="onSubmitComparison"
      >
        <v-card>
          <dialog-title
            v-model="openComparisonEditor"
            :title="`${comparisonIndex > -1 ? 'Add' : 'Edit'} Comparison`"
            :disabled="loading"
            :loading="loading"
          ></dialog-title>
          <v-card-subtitle>
            Fill out the form below to configure a
            <strong>Grouping Comparison</strong>.
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left grey darken-4 white--text">
                      Question Key
                    </th>
                    <th class="text-left grey darken-4 white--text w-100">
                      Criteria
                    </th>
                    <th class="text-center grey darken-4 white--text">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(setting, ii) in comparison"
                    :key="ii"
                    :class="{ 'grey lighten-3': ii % 2 }"
                  >
                    <td class="text-no-wrap">{{ setting.key }}</td>
                    <td>
                      <v-chip
                        small
                        color="primary"
                        class="mr-1 mb-1"
                        v-for="(grouping, iii) in setting.groupedOn"
                        :key="iii"
                      >
                        <span class="font-weight-bold mr-1">
                          {{ grouping.key }}:</span
                        >
                        {{ grouping.value }}
                      </v-chip>
                    </td>
                    <td class="text-no-wrap">
                      <v-btn icon small><v-icon>$edit</v-icon></v-btn>
                      <v-btn icon small><v-icon>$delete</v-icon></v-btn>
                      <v-btn
                        :disabled="ii === 0"
                        icon
                        small
                        @click="sortSettingUp(ii)"
                        ><v-icon>$up</v-icon></v-btn
                      >
                      <v-btn
                        :disabled="comparison.length - 1 === ii"
                        icon
                        small
                        @click="sortSettingDown(ii)"
                        ><v-icon>$down</v-icon></v-btn
                      >
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              type="button"
              elevation="0"
              text
              @click="openComparisonEditor = false"
              >Cancel</v-btn
            >
            <v-btn type="submit" color="primary">Save</v-btn>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>
  </v-container>
</template>

<style lang="scss">
.v-divider {
  &.dashed-v,
  &.dashed-h {
    border-style: dashed !important;
    border-width: 3px !important;
  }

  &.dashed-h {
    border-top: 0 !important;
  }
  &.dashed-v {
    border-left: 1 !important;
  }
}
</style>

<script>
import Vue from "vue";
import DialogTitle from "../../components/DialogTitle.vue";

import dateTimeService from "../../services/dateTimeService";
import surveyService from "../../services/surveyService";

import { reportTypes } from "../../constants/Survey";

const newItem = () => ({
  name: "",
  type: reportTypes.itemAnalysis,
  icon: "chart",
  groupBy: [],
  groupComparisons: [],
});

const newGroupComparison = () => ({
  key: null,
  groupedOn: [],
});

const newPair = () => ({
  key: null,
  value: null,
});

export default {
  name: "Reporting",
  components: {
    DialogTitle,
  },
  data: () => ({
    survey: {},
    pendingStateChanges: 0,
    aggAlert: false,

    openEditor: false,
    itemIndex: -1,
    item: newItem(),
    newGrouping: "",

    openComparisonEditor: false,
    comparisonIndex: -1,
    comparison: [],

    openComparisonSettingEditor: false,
    comparisonSettingIndex: -1,
    comparisonSetting: newGroupComparison(),
    newComparisonSettingGrouping: newPair(),
  }),
  computed: {
    tenantId() {
      return this.$store.state.tenantId;
    },
    isNew() {
      return this.itemIndex === -1;
    },
    typeOptions() {
      let options = [];
      for (let key in reportTypes) {
        options.push(reportTypes[key]);
      }
      return options;
    },
    iconOptions() {
      let options = [];

      for (const key in this.$vuetify.icons.values) {
        options.push(key);
      }
      return options;
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    breadcrumbs() {
      return [
        {
          text: "All Surveys",
          exact: true,
          to: { name: "surveys" },
        },
        {
          text: this.loading
            ? "Loading..."
            : this.survey.name ?? "Unnamed Survey",
          disable: true,
        },
      ];
    },
  },
  watch: {
    openComparisonEditor(val, prev) {
      if (val != prev) this.openEditor = !val;
    },
  },
  methods: {
    load() {
      this.pendingStateChanges++;

      surveyService
        .get(this.tenantId, this.$route.params.id)
        .then(
          (result) => {
            this.survey = result;
            this.survey.reports =
              this.survey.reports?.map((x) => {
                x.formattedGeneratedAt =
                  dateTimeService.formatDateTime(x.generatedAt) ?? "--";
                return x;
              }) ?? [];
          },
          (e) => {
            console.warn(e);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading access keys. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    edit(i) {
      if (i > -1)
        this.item = JSON.parse(JSON.stringify(this.survey.reports[i]));
      else this.item = newItem();

      this.itemIndex = i;

      this.$refs.observer?.reset();
      this.openEditor = true;
    },
    addGrouping() {
      if (this.newGrouping === "") return;

      if (this.item.groupBy.length === 0) {
        this.item.groupBy.push(this.newGrouping);
      } else {
        Vue.set(this.item.groupBy, 0, this.newGrouping);
      }

      this.newGrouping = "";
      // Temporarily disabled. Only single key groupings work.
      //   if (!this.item.groupBy.some((x) => x === this.newGrouping)) {
      //     this.item.groupBy.push(this.newGrouping);
      //     this.newGrouping = "";
      //   }
    },
    removeGrouping(index) {
      this.item.groupBy.splice(index, 1);
    },
    async onSubmit() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid || this.loading) return;

      this.pendingStateChanges++;

      let itemData = JSON.parse(JSON.stringify(this.item));
      itemData.generatedAt = null;
      let surveyData = JSON.parse(JSON.stringify(this.survey));

      if (this.isNew) surveyData.reports.push(itemData);
      else surveyData.reports[this.itemIndex] = itemData;

      surveyService
        .update(this.tenantId, surveyData.id, surveyData)
        .then(
          () => {
            this.load();
            this.openEditor = false;
            this.$store.dispatch(
              "notifications/success",
              `Successfully saved report.`
            );
          },
          (e) => {
            console.warn(e);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while save report. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    refreshAggs() {
      this.pendingStateChanges++;
      this.aggAlert = true;

      surveyService
        .refreshAggregates(this.tenantId, this.$route.params.id)
        .then(
          () => {
            this.load();
            this.$store.dispatch(
              "notifications/success",
              `Successfully refreshed reports.`
            );
          },
          (e) => {
            console.warn(e);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while refreshing reports. Please try again later."
            );
          }
        )
        .finally(() => {
          this.aggAlert = false;
          this.pendingStateChanges--;
        });
    },
    editComparison(i) {
      if (i > -1)
        this.comparison = JSON.parse(
          JSON.stringify(this.item.groupComparisons[i])
        );
      else this.comparison = [];

      this.comparisonIndex = i;

      this.$refs.comparisonObserver?.reset();
      this.openComparisonEditor = true;
    },
    sortSettingUp(i) {
      arr_move(this.comparison, i, i - 1);
    },
    sortSettingDown(i) {
      arr_move(this.comparison, i, i + 1);
    },    
    deleteSetting(i) {
      arr_move(this.comparison, i, i + 1);
    },
  },
  mounted() {
    this.load();
  },
};

function arr_move(arr, currentIndex, targetIndex) {
  let data = JSON.parse(JSON.stringify(arr[currentIndex]));

  // remove at old index
  arr.splice(currentIndex, 1);

  // insert at new index
  arr.splice(targetIndex, 0, data);
}
</script>
